/**
 * The signed-in session: what is kept, where, and when it has to be renewed.
 *
 * Split from `cognitoTokenProvider.ts` so the decisions — is this token still
 * usable, what does it say the user's address is — are pure functions with
 * tests, rather than something only reachable through a real redirect.
 */

export interface Session {
  readonly accessToken: string;
  /** Epoch milliseconds. Absolute, so a clock-skewed `expires_in` is resolved once. */
  readonly expiresAt: number;
  readonly refreshToken?: string;
  /** From the token's `email` claim, for the header. Display only. */
  readonly email?: string;
}

/**
 * Renew this far ahead of expiry. A token that passes the check here still has
 * to survive the network hop and the API's own validation, so expiring
 * "later this second" has to count as expired.
 */
const REFRESH_MARGIN_MS = 60_000;

export function isUsable(session: Session, nowMs: number): boolean {
  return session.expiresAt - REFRESH_MARGIN_MS > nowMs;
}

export function expiresAtFrom(nowMs: number, expiresInSeconds: number): number {
  return nowMs + expiresInSeconds * 1000;
}

/**
 * The `email` claim out of the access token, for showing who is signed in.
 *
 * **Not a security check.** The signature is not verified here and must not
 * be: the API is what validates the token. This only saves a round trip for a
 * string in the header. A token that cannot be decoded yields `undefined`
 * rather than throwing, because a missing name in the corner is not a reason
 * to fail a sign-in.
 *
 * The claim is not standard on an access token; a pre-token-generation
 * trigger adds it (sc-178487), which is also what the API reads for
 * `uploadedBy`.
 */
export function readEmailClaim(accessToken: string): string | undefined {
  const payload = accessToken.split(".")[1];
  if (payload === undefined) {
    return undefined;
  }
  try {
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as { email?: unknown };
    return typeof claims.email === "string" ? claims.email : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Where a session and the in-flight PKCE values live.
 *
 * An interface rather than `sessionStorage` directly so the provider is
 * testable, and so the choice of storage is stated in one place.
 */
export interface SessionStorage {
  read(key: string): string | null;
  write(key: string, value: string): void;
  remove(key: string): void;
}

/**
 * `sessionStorage`, not `localStorage`: the tokens should not outlive the tab.
 * Closing the browser then ends the session, which is what a legal reviewer on
 * a shared machine would expect.
 *
 * Reads and writes are wrapped because storage throws rather than returning
 * null when it is blocked (Safari private browsing, a locked-down profile).
 * Losing the session there means signing in again — worse than working, far
 * better than a blank page.
 */
export function browserSessionStorage(
  storage: Storage = sessionStorage,
): SessionStorage {
  return {
    read(key) {
      try {
        return storage.getItem(key);
      } catch {
        return null;
      }
    },
    write(key, value) {
      try {
        storage.setItem(key, value);
      } catch {
        // Ignored on purpose: see above.
      }
    },
    remove(key) {
      try {
        storage.removeItem(key);
      } catch {
        // Ignored on purpose: see above.
      }
    },
  };
}

export const SESSION_KEY = "contract-manager.session";

export function readSession(storage: SessionStorage): Session | null {
  const raw = storage.read(SESSION_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (
      typeof parsed.accessToken !== "string" ||
      typeof parsed.expiresAt !== "number"
    ) {
      return null;
    }
    return {
      accessToken: parsed.accessToken,
      expiresAt: parsed.expiresAt,
      ...(typeof parsed.refreshToken === "string"
        ? { refreshToken: parsed.refreshToken }
        : {}),
      ...(typeof parsed.email === "string" ? { email: parsed.email } : {}),
    };
  } catch {
    // A half-written or hand-edited entry is treated as no session.
    return null;
  }
}

export function writeSession(storage: SessionStorage, session: Session): void {
  storage.write(SESSION_KEY, JSON.stringify(session));
}

export function clearSession(storage: SessionStorage): void {
  storage.remove(SESSION_KEY);
}
